import * as React from "react";
import { ClockIcon } from "@radix-ui/react-icons";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { ScrollArea, ScrollBar } from "./scroll-area";

interface TimePickerProps {
  value?: string; // "HH:mm"
  onChange?: (value: string) => void;
  className?: string;
  disabled?: boolean;
  id?: string;
  placeholder?: string;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);

function parseTime(value: string | undefined): { hour: number; minute: number } {
  if (!value) return { hour: 9, minute: 0 };
  const [h, m] = value.split(":").map(Number);
  return { hour: isNaN(h) ? 9 : h, minute: isNaN(m) ? 0 : m };
}

function formatTime(hour: number, minute: number): string {
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

export function TimePicker({
  value,
  onChange,
  className,
  disabled,
  id,
  placeholder = "Pick time",
}: TimePickerProps) {
  const [open, setOpen] = React.useState(false);
  const { hour, minute } = parseTime(value);

  const handleHour = (h: number) => {
    onChange?.(formatTime(h, minute));
  };

  const handleMinute = (m: number) => {
    onChange?.(formatTime(hour, m));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          disabled={disabled}
          className={cn(
            "justify-start text-left font-normal",
            !value && "text-muted-foreground",
            className
          )}
        >
          <ClockIcon className="mr-2 h-4 w-4 shrink-0" />
          {value ? value : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex h-[220px] divide-x">
          {/* Hours */}
          <ScrollArea className="w-[4.5rem]">
            <div className="flex flex-col p-1">
              {HOURS.map((h) => (
                <Button
                  key={h}
                  size="icon"
                  variant={hour === h ? "default" : "ghost"}
                  className="w-full shrink-0 aspect-square text-sm"
                  onClick={() => handleHour(h)}
                >
                  {String(h).padStart(2, "0")}
                </Button>
              ))}
            </div>
            <ScrollBar orientation="vertical" />
          </ScrollArea>

          {/* Minutes */}
          <ScrollArea className="w-[4.5rem]">
            <div className="flex flex-col p-1">
              {MINUTES.map((m) => (
                <Button
                  key={m}
                  size="icon"
                  variant={minute === m ? "default" : "ghost"}
                  className="w-full shrink-0 aspect-square text-sm"
                  onClick={() => handleMinute(m)}
                >
                  {String(m).padStart(2, "0")}
                </Button>
              ))}
            </div>
            <ScrollBar orientation="vertical" />
          </ScrollArea>
        </div>
      </PopoverContent>
    </Popover>
  );
}
